import { FilePdfOutlined, PlusOutlined } from '@ant-design/icons'
import type { Home } from '@lmc/ts-common'
import {
  Button,
  FilterContainer,
  MultiSelect,
  PageHeader,
  RangePicker,
  Spinner
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Tabs } from 'antd'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import usePdfExport from 'hooks/usePdfExport'
import useResident from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import Actions from '../../../../../actions'
import Operations from '../../../../../operations'
import {
  EMARPaywall,
  EMARScreenEmptyState
} from '../../ResidentEmarScreens/components'
import { AddMedicationButton } from '../componentsV2/AddMedicationButton'
import { AdministrationRecordDrawer } from '../componentsV2/AdministrationRecordDrawer'
import AdministrationModal from '../componentsV2/modalsV2/AdministrationModal'
import BackSignMissedMedicationModal from '../componentsV2/modalsV2/BackSignMissedMedicationModal'
import PageWrapper from '../componentsV2/PageWrapper'
import { useMarChart } from '../hooks/useMarChart'
import type { MarChartCell, MedicationTableRow, StockRecord } from '../types'
import { transformMarChartData } from '../utils/administrationUtils'
import { buildStockRecord } from '../utils/buildStockRecord'
import Legend from './Legend'
import MedicationsTable from './MedicationsTable'

interface EMARResidentScreenV2Props {
  testId?: string
}

const EMARResidentScreenV2 = ({ testId }: EMARResidentScreenV2Props = {}) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const { id: residentId } = useParams<{ id: string }>()
  const dispatch = useDispatch()
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const user = useSelector(
    (state: { global: { user?: { medicationPermission?: boolean } } }) =>
      state.global.user
  )

  const { resident, isLoading: isLoadingResident } = useResident({
    id: residentId
  })

  const flags = useFeatureFlags({
    flagNames: ['emar_back_sign_missed']
  })?.data
  const allowBackSignMissed = flags?.emar_back_sign_missed?.enabled ?? false

  const [startDate, setStartDate] = useState(
    dayjs().subtract(14, 'days').startOf('day')
  )
  const [endDate, setEndDate] = useState(dayjs().endOf('day'))

  const {
    data: marChartData,
    isLoading: isLoadingMarChart,
    refetch: refetchMarChart
  } = useMarChart({
    residentId: residentId || '',
    startDate,
    endDate,
    enabled: !!residentId
  })

  const [selectedMedications, setSelectedMedications] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState<string>('scheduled')
  const [administrationDrawerOpen, setAdministrationDrawerOpen] =
    useState(false)
  const [selectedRecord, setSelectedRecord] = useState<
    StockRecord | undefined
  >()
  const [selectedMedicationName, setSelectedMedicationName] =
    useState<string>('')
  const [selectedControlledDrug, setSelectedControlledDrug] =
    useState<boolean>(false)
  const [administrationModalOpen, setAdministrationModalOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<StockRecord | null>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [backSignModalOpen, setBackSignModalOpen] = useState(false)
  const [backSignRecord, setBackSignRecord] = useState<
    StockRecord | undefined
  >()
  const [backSignRoundTime, setBackSignRoundTime] = useState<
    string | undefined
  >()
  const [backSignDose, setBackSignDose] = useState<string | undefined>()
  const [backSignAltDose, setBackSignAltDose] = useState<string | undefined>()
  const pdfContentRef = useRef<HTMLDivElement>(null)

  const medicationOptions = useMemo(() => {
    if (!marChartData?.medications) return []
    return marChartData.medications.map((med: any) => ({
      value: med.residentMedicationId,
      label: med.medicationName
    }))
  }, [marChartData])

  const scheduledTableData = useMemo(() => {
    const data = transformMarChartData(
      marChartData,
      'scheduled',
      startDate,
      endDate
    )
    if (selectedMedications.length === 0) return data
    return data.filter((row) => {
      return selectedMedications.includes(row.residentMedicationId)
    })
  }, [marChartData, startDate, endDate, selectedMedications])

  const prnTableData = useMemo(() => {
    const data = transformMarChartData(marChartData, 'prn', startDate, endDate)
    if (selectedMedications.length === 0) return data
    return data.filter((row) => {
      return selectedMedications.includes(row.residentMedicationId)
    })
  }, [marChartData, startDate, endDate, selectedMedications])

  const residentName = resident
    ? `${resident.name.first} ${resident.name.last}`
    : ''

  const handleAddMedication = () => {
    if (!resident) return
    dispatch(
      Operations.loadList(
        'emar-round-timings',
        undefined,
        true,
        '/emar/legacy/emar-round-timings'
      )
    )
    dispatch(Operations.openModal({ modalId: 'addEmarMedication' }))
    dispatch(
      Actions.addForm({
        formId: 'addEmarMedication',
        initial: { resident: resident.id }
      })
    )
  }

  const handleOpenOnboardingModal = () => {
    dispatch(
      Operations.loadList(
        'emar-round-timings',
        undefined,
        true,
        '/emar/legacy/emar-round-timings'
      )
    )
    dispatch(Operations.openModal({ modalId: 'onboardingEmar' }))
    dispatch(
      Actions.addForm({
        formId: 'onboardingEmar'
      })
    )
  }

  const handleDateChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setStartDate(dates[0].startOf('day'))
      setEndDate(dates[1].endOf('day'))
    } else {
      setStartDate(dayjs().subtract(14, 'days').startOf('day'))
      setEndDate(dayjs().endOf('day'))
    }
  }

  const handleMedicationChange = (medications: string[]) => {
    setSelectedMedications(medications)
  }

  const handleTabChange = (key: string) => {
    setActiveTab(key)
  }

  const handleCellClick = (record: MedicationTableRow, dateKey: string) => {
    const cellData = record[dateKey] as MarChartCell | undefined
    if (!cellData?.outcomes || cellData.outcomes.length === 0) return

    const firstOutcome = cellData.outcomes[0]
    if (firstOutcome.action === 'PAUSED') return
    if (firstOutcome.action === 'MISSED') return

    const stockRecord = buildStockRecord({
      outcome: firstOutcome,
      row: record,
      dateKey
    })
    if (!stockRecord) return

    setSelectedRecord(stockRecord)
    setSelectedMedicationName(record.medication?.name || '')
    setSelectedControlledDrug(record.medication?.isControlledDrug || false)
    setAdministrationDrawerOpen(true)
  }

  const handleCloseAdministrationDrawer = () => {
    setAdministrationDrawerOpen(false)
    setSelectedRecord(undefined)
    setSelectedMedicationName('')
    setSelectedControlledDrug(false)
  }

  const handleEditFromDrawer = (record: StockRecord) => {
    if (record.type === 'administration') {
      setIsEditMode(true)
      setEditingRecord(record)
      setAdministrationModalOpen(true)
    }
  }

  const handleEditAdministrationCancel = () => {
    setAdministrationModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleEditAdministrationSuccess = () => {
    setAdministrationModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
    setAdministrationDrawerOpen(false)
    refetchMarChart()
  }

  const handleMissedCellBackSign = (
    record: MedicationTableRow,
    dateKey: string
  ) => {
    const cellData = record[dateKey] as MarChartCell | undefined
    if (!cellData?.outcomes || cellData.outcomes.length === 0) return

    const stockRecord = buildStockRecord({
      outcome: cellData.outcomes[0],
      row: record,
      dateKey
    })
    if (!stockRecord) return

    setBackSignRoundTime(stockRecord.roundTime)
    setBackSignDose(record.dose || undefined)
    setBackSignAltDose(record.altDose || undefined)
    setSelectedMedicationName(record.medication?.name || '')
    setBackSignRecord(stockRecord)
    setBackSignModalOpen(true)
  }

  const handleBackSignCancel = () => {
    setBackSignModalOpen(false)
    setBackSignRecord(undefined)
    setBackSignDose(undefined)
    setBackSignAltDose(undefined)
  }

  const handleBackSignSuccess = () => {
    setBackSignModalOpen(false)
    setBackSignRecord(undefined)
    setBackSignDose(undefined)
    setBackSignAltDose(undefined)
    setAdministrationDrawerOpen(false)
    refetchMarChart()
  }

  const pdfTitle = `${residentName}'s ${t('EMAR')} - ${startDate.format('DD-MM-YYYY')} to ${endDate.format('DD-MM-YYYY')}`
  const handleDownloadPdf = usePdfExport(
    pdfContentRef as React.RefObject<HTMLInputElement>,
    pdfTitle,
    setIsDownloading
  )

  const tabItems = [
    {
      key: 'scheduled',
      label: <Text>{t('SCHEDULED')}</Text>,
      children: (
        <MedicationsTable
          startDate={startDate}
          endDate={endDate}
          type="scheduled"
          residentFirstName={resident?.name?.first || ''}
          onCellClick={handleCellClick}
          data={scheduledTableData}
          onMissedBackSign={
            allowBackSignMissed && !!user?.medicationPermission
              ? handleMissedCellBackSign
              : undefined
          }
        />
      )
    },
    {
      key: 'prn',
      label: <Text>{t('PRN')}</Text>,
      children: (
        <MedicationsTable
          startDate={startDate}
          endDate={endDate}
          type="prn"
          residentFirstName={resident?.name?.first || ''}
          onCellClick={handleCellClick}
          data={prnTableData}
          onMissedBackSign={
            allowBackSignMissed && !!user?.medicationPermission
              ? handleMissedCellBackSign
              : undefined
          }
        />
      )
    }
  ]

  if (isLoadingResident || !resident || isLoadingMarChart) {
    return (
      <PageWrapper>
        <Spinner style={{ marginTop: '80px' }} />
      </PageWrapper>
    )
  }

  if (!home?.features?.eMAR?.enabled) return <EMARPaywall />

  return (
    <div ref={pdfContentRef}>
      <PageHeader title={`${residentName}'s ${t('EMAR')}`} sticky>
        <HeaderButtonContainer>
          {home?.features?.emarOnboarded?.enabled && (
            <Button
              onClick={handleDownloadPdf}
              variant="default"
              isLoading={isDownloading}
              disabled={isDownloading}
              data-testid="emar-download-pdf-button"
              style={{ whiteSpace: 'nowrap' }}
            >
              <FilePdfOutlined />
              {t('DOWNLOAD_PDF')}
            </Button>
          )}
          <AddMedicationButton
            resident={resident}
            onClick={handleAddMedication}
            style={{ whiteSpace: 'nowrap' }}
            dataTestId="emar-add-medication-btn"
          />
        </HeaderButtonContainer>
      </PageHeader>

      {!home?.features?.emarOnboarded?.enabled && (
        <EMARScreenEmptyState
          resident={resident}
          emarOnboarded={home && home.features?.emarOnboarded?.enabled}
          openOnboardingModal={handleOpenOnboardingModal}
        />
      )}

      {home?.features?.emarOnboarded?.enabled && (
        <PageWrapper>
          <FilterContainer title="">
            <>
              <RangePicker
                defaultValue={[startDate, endDate]}
                value={[startDate, endDate]}
                onChange={handleDateChange}
                format="DD-MM-YYYY"
                data-testid="emar-date-range-picker"
              />
              <MultiSelect
                title={t('MEDICATION')}
                options={medicationOptions}
                values={selectedMedications}
                onChange={handleMedicationChange}
                hasClear
                hasSearch
                hasSelectAll
                data-testid="emar-medication-filter"
              />
            </>
          </FilterContainer>
          <StyledTabs
            defaultActiveKey="scheduled"
            activeKey={activeTab}
            items={tabItems}
            onChange={handleTabChange}
            tabBarExtraContent={<Legend testId={testId} />}
            data-testid="emar-medications-tabs"
          />
          <AdministrationRecordDrawer
            isVisible={administrationDrawerOpen}
            onClose={handleCloseAdministrationDrawer}
            record={selectedRecord}
            medicationName={selectedMedicationName}
            controlledDrug={selectedControlledDrug}
            onEdit={handleEditFromDrawer}
          />
          {administrationModalOpen && (
            <AdministrationModal
              open={administrationModalOpen}
              medicationName={selectedMedicationName}
              record={editingRecord || undefined}
              onCancel={handleEditAdministrationCancel}
              onSuccess={handleEditAdministrationSuccess}
            />
          )}
          {backSignModalOpen && (
            <BackSignMissedMedicationModal
              open={backSignModalOpen}
              medicationName={selectedMedicationName}
              record={backSignRecord}
              roundTime={backSignRoundTime}
              dose={backSignDose}
              altDose={backSignAltDose}
              onCancel={handleBackSignCancel}
              onSuccess={handleBackSignSuccess}
            />
          )}
        </PageWrapper>
      )}
    </div>
  )
}

const HeaderButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`

const StyledTabs = styled(Tabs)`
  flex: 1;
  margin-bottom: 24px;
`

export default EMARResidentScreenV2
